import { useCallback, useMemo } from "react";

import type { E164Number } from "libphonenumber-js/core";
import isNil from "lodash/isNil";
import PhoneInput from "react-phone-number-input/react-native-input";

import { ProfileTestID } from "@/constants/TestID";
import {
  AddressLine1Field,
  AddressLine2Field,
  CityField,
  StateField,
  ZipField,
} from "@/ui/components/profile/edit/ContactInfoAddress";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import { FAIconName, LabeledInput, TextField } from "@/ui/core";
import type { PermissionChecker } from "@/utils/profile/permissions.util";
import { isEditable, isViewable } from "@/utils/profile/permissions.util";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = ProfileTestID.ContactInfo;

const EmailField = ({
  email,
  setEmail,
  trackFieldPressIn,
  userPermissions,
}: {
  email: string;
  setEmail: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("email"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Email address"
        testID={joinTestIDs(baseTestID, "email-label")}
      >
        <TextField
          autoComplete="email"
          icon={FAIconName.Envelope}
          isDisabled={!isEditable(userPermissions("email"))}
          onChange={(value) => {
            setEmail(value);
          }}
          onPressIn={() => trackFieldPressIn?.(EmailField.name)}
          placeholder="Email address"
          testID={joinTestIDs(baseTestID, "email")}
          value={email}
        />
      </LabeledInput>
    );
  }, [email, setEmail, userPermissions, trackFieldPressIn]);
};

const CellPhoneField = ({
  cellPhone,
  setCellPhone,
  trackFieldPressIn,
  userPermissions,
}: {
  cellPhone: string;
  setCellPhone: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  // PhoneInput supports passing an inputComponent; however it will rename its onChange -> onChangeText
  // here, we rename it back to onChange as supported by our TextField
  // see: https://gitlab.com/catamphetamine/react-phone-number-input#react-native
  const TextFieldWrapper = useCallback(
    ({
      onChangeText,
      value,
    }: {
      onChangeText: (value: E164Number) => void;
      value?: E164Number | string;
    }) => {
      const isDisabled = !isEditable(userPermissions("cellPhone"));
      return (
        <TextField
          autoComplete="tel"
          icon={FAIconName.Mobile}
          isDisabled={isDisabled}
          onChange={(newValue: string) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
            onChangeText(newValue as E164Number);
          }}
          onPressIn={() => trackFieldPressIn?.(CellPhoneField.name)}
          placeholder="Cell phone"
          testID={joinTestIDs(baseTestID, "cellPhone")}
          value={value}
        />
      );
    },
    [trackFieldPressIn, userPermissions],
  );

  return useMemo(() => {
    if (!isViewable(userPermissions("cellPhone"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Cell phone"
        testID={joinTestIDs(baseTestID, "cellPhone-label")}
      >
        <PhoneInput
          defaultCountry="US"
          inputComponent={TextFieldWrapper}
          onChange={(value?: E164Number) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
            setCellPhone(value as string);
          }}
          value={cellPhone}
        />
      </LabeledInput>
    );
  }, [cellPhone, setCellPhone, userPermissions, TextFieldWrapper]);
};

const ContactPreferencesField = ({
  contactPreferences,
  options,
  setContactPreferences,
  trackFieldPressIn,
  userPermissions,
}: {
  contactPreferences?: ContactPreference[];
  options: MultiSelectOption[];
  setContactPreferences: (value: ContactPreference[]) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("contactPreferences"))) {
      return null;
    }

    const defaultSelectedValues = isNil(contactPreferences)
      ? []
      : contactPreferences.map((preference: ContactPreference) => ({
          displayText: preference,
          value: preference,
        }));

    return (
      <LabeledInput
        leftText="Contact preferences"
        testID={joinTestIDs(baseTestID, "contact-preferences-label")}
      >
        <MultiSelectTextField
          defaultSelectedValues={defaultSelectedValues}
          isDisabled={!isEditable(userPermissions("contactPreferences"))}
          modalTitle="Contact preferences"
          onPressIn={() => trackFieldPressIn?.(ContactPreferencesField.name)}
          onValuesChange={(selections: MultiSelectOption[]) =>
            setContactPreferences(
              selections.map(
                (option: MultiSelectOption) =>
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
                  option.value as ContactPreference,
              ),
            )
          }
          options={options}
          testID={joinTestIDs(baseTestID, "contact-preferences")}
          value={contactPreferences}
        />
      </LabeledInput>
    );
  }, [
    contactPreferences,
    options,
    setContactPreferences,
    userPermissions,
    trackFieldPressIn,
  ]);
};

export {
  AddressLine1Field,
  AddressLine2Field,
  CellPhoneField,
  CityField,
  ContactPreferencesField,
  EmailField,
  StateField,
  ZipField,
};
